import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, OutlinedInput } from '@mui/material';
import { Stack } from '@mui/system';
import { FC, useEffect } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { EditModalState } from '../../../../../modal/useEditModal';
import { z } from 'zod';

import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useTypedForm } from '../../../../../../../shared/fe/hooks/useForm';
import { graphql } from '../../../../../gql';
import { useApiMutation } from '../../../../../query/hooks';
import { FormField } from '../../../../../form/FormField';
import { FormErrors } from '../../../../../form/FormErrors';
import { Tag } from '../../../../../settings/organization/resources/tags/TagsResourcePage';
import { useSnackbar } from '../../../../../store/snackbar';

const createTagMutation = graphql(`
	mutation createTagMutation($name: String!) {
		createTag(data: { name: $name }) {
			ok
			errorMessage
		}
	}
`);

const updateTagMutation = graphql(`
	mutation updateTagMutation($id: UUID!, $name: String!) {
		updateTag(by: { id: $id }, data: { name: $name }) {
			ok
			errorMessage
		}
	}
`);

type Props = {
	readonly state: EditModalState<Tag>;
	readonly onSave: () => void;
	readonly onCancel: () => void;
};

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().nonempty(intl.formatMessage({ id: 'form.tags.error.empty-name' })),
	});

type FormValues = z.TypeOf<ReturnType<typeof createFormSchema>>;

export const TagsEdit: FC<Props> = ({ state, onSave, onCancel }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();
	const form = useTypedForm({
		schema: createFormSchema(intl),
	});
	const { mutate: createTag } = useApiMutation(createTagMutation, {
		onSuccess: async (result) => {
			mutationResponseHandler({
				result: result.createTag.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'resources.tags.create.success' }),
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.createTag.errorMessage,
				openSnackbar,
			});
		},
	});
	const { mutate: updateTag } = useApiMutation(updateTagMutation, {
		onSuccess: async (result) => {
			mutationResponseHandler({
				result: result.updateTag.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'resources.tags.update.success' }),
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updateTag.errorMessage,
				openSnackbar,
			});
		},
	});

	const onSubmit = (data: FormValues) => {
		if (state.operation === 'CREATE') {
			createTag({ name: data.name }, { onSuccess: onSave });
		}
		if (state.operation === 'UPDATE' && state.data != null) {
			updateTag({ id: state.data.id, name: data.name }, { onSuccess: onSave });
		}
	};

	useEffect(() => {
		if (state.isOpen && state.operation === 'CREATE') {
			form.reset({
				name: '',
			});
		}
		if (state.isOpen && state.operation === 'UPDATE' && state.data != null) {
			form.reset({
				name: state.data.name,
			});
		}
	}, [state, form]);

	return (
		<Dialog maxWidth={'sm'} keepMounted fullWidth onClose={onCancel} open={state.isOpen}>
			<form noValidate onSubmit={form.handleSubmit(onSubmit)}>
				<DialogTitle>
					{state.operation === 'CREATE' ? (
						<FormattedMessage id={'form.tags.title.create'} />
					) : (
						<FormattedMessage id={'form.tags.title.update'} />
					)}
				</DialogTitle>
				<Divider />
				<DialogContent>
					<Grid container spacing={3}>
						<FormField
							form={form}
							name={'name'}
							label={intl.formatMessage({ id: 'form.tags.label.name' })}
							render={(props) => <OutlinedInput {...props} />}
						/>

						<FormErrors form={form} />
					</Grid>
				</DialogContent>
				<Divider />
				<DialogActions sx={{ p: 2.5 }}>
					<Stack direction={'row'} spacing={2} alignItems={'center'} width={'100%'} justifyContent={'end'}>
						<Button color={'secondary'} onClick={onCancel}>
							<FormattedMessage id={'common.cancel'} />
						</Button>
						<Button type={'submit'} variant={'contained'} disabled={form.formState.isSubmitting}>
							{state.operation === 'CREATE' ? <FormattedMessage id={'common.create'} /> : <FormattedMessage id={'common.save'} />}
						</Button>
					</Stack>
				</DialogActions>
			</form>
		</Dialog>
	);
};
